// OptionChainTable — Calls/puts side-by-side grid (Angel One Light Theme)
// Handles both optionGreek API format and market data quote format
import React from 'react';

// Helper to extract price from various API response formats
function getPrice(item) {
  if (!item) return null;
  const v = parseFloat(item.ltp) || parseFloat(item.lastPrice) || parseFloat(item.last_price) || null;
  return v;
}

function getOI(item) {
  if (!item) return null;
  return parseFloat(item.opnInterest) || parseFloat(item.openInterest) || parseFloat(item.oi) || null;
}

function getOIChange(item) {
  if (!item) return null;
  return parseFloat(item.changeinOpenInterest) || parseFloat(item.oiChange) || null;
}

function getVolume(item) {
  if (!item) return null;
  return parseFloat(item.tradeVolume) || parseFloat(item.totalTradedVolume) || parseFloat(item.volume) || null;
}

function getIV(item) {
  if (!item) return null;
  return parseFloat(item.impliedVolatility) || parseFloat(item.iv) || null;
}

export default function OptionChainTable({ strikes, atmStrike, onSelect, onTrade }) {
  if (!strikes || strikes.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center text-xs text-gray-400">
        No option chain data available
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden overflow-x-auto">
      <table className="w-full text-[10px]">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th colSpan={5} className="px-2 py-2 text-center text-green-600 font-semibold uppercase tracking-wider">CALLS</th>
            <th className="px-2 py-2 text-center text-blue-600 font-semibold uppercase tracking-wider">Strike</th>
            <th colSpan={5} className="px-2 py-2 text-center text-red-500 font-semibold uppercase tracking-wider">PUTS</th>
          </tr>
          <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold uppercase tracking-wider">
            <th className="px-2 py-1.5 text-right">OI</th>
            <th className="px-2 py-1.5 text-right">Chg OI</th>
            <th className="px-2 py-1.5 text-right">Vol</th>
            <th className="px-2 py-1.5 text-right">IV</th>
            <th className="px-2 py-1.5 text-right">LTP</th>
            <th className="px-2 py-1.5 text-center bg-gray-100">Strike</th>
            <th className="px-2 py-1.5">LTP</th>
            <th className="px-2 py-1.5">IV</th>
            <th className="px-2 py-1.5">Vol</th>
            <th className="px-2 py-1.5">Chg OI</th>
            <th className="px-2 py-1.5">OI</th>
          </tr>
        </thead>
        <tbody>
          {strikes.map((row) => {
            const call = row.call || {};
            const put = row.put || {};
            const isATM = atmStrike && row.strike === atmStrike;

            const callPrice = getPrice(call);
            const putPrice = getPrice(put);
            const callOI = getOI(call);
            const putOI = getOI(put);
            const callOIChg = getOIChange(call);
            const putOIChg = getOIChange(put);
            const callVol = getVolume(call);
            const putVol = getVolume(put);
            const callIV = getIV(call);
            const putIV = getIV(put);

            return (
              <tr
                key={row.strike}
                className={`border-b hover:bg-gray-50 group transition-colors ${
                  isATM
                    ? 'bg-blue-50 border-y border-blue-200'
                    : 'border-gray-100'
                }`}
              >
                <td className="px-2 py-1.5 text-right font-mono text-gray-600">{formatNum(callOI)}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${(callOIChg || 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatNum(callOIChg)}
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-gray-600">{formatNum(callVol)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-gray-400">{callIV ? callIV.toFixed(1) : '—'}</td>
                <td className="px-2 py-1.5 text-right">
                  <button
                    onClick={() => onTrade && onTrade({ ...call, optionType: 'CE' }, 'BUY')}
                    className="font-mono font-semibold text-gray-900 hover:text-green-600 cursor-pointer transition-colors"
                  >
                    {callPrice ? callPrice.toFixed(2) : '—'}
                  </button>
                </td>
                <td className={`px-2 py-1.5 text-center font-mono font-bold text-blue-600 ${isATM ? 'bg-blue-100' : 'bg-gray-50'}`}>
                  {row.strike}
                </td>
                <td className="px-2 py-1.5">
                  <button
                    onClick={() => onTrade && onTrade({ ...put, optionType: 'PE' }, 'BUY')}
                    className="font-mono font-semibold text-gray-900 hover:text-red-500 cursor-pointer transition-colors"
                  >
                    {putPrice ? putPrice.toFixed(2) : '—'}
                  </button>
                </td>
                <td className="px-2 py-1.5 font-mono text-gray-400">{putIV ? putIV.toFixed(1) : '—'}</td>
                <td className="px-2 py-1.5 font-mono text-gray-600">{formatNum(putVol)}</td>
                <td className={`px-2 py-1.5 font-mono ${(putOIChg || 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatNum(putOIChg)}
                </td>
                <td className="px-2 py-1.5 font-mono text-gray-600">{formatNum(putOI)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatNum(n) {
  if (!n && n !== 0) return '—';
  if (Math.abs(n) >= 100000) return (n / 100000).toFixed(1) + 'L';
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toLocaleString('en-IN');
}
